import { format } from "date-fns";
import { geoNames } from "./countriesGeonames";
import { useSelector } from "react-redux";

// function use to add the exponent to the number like 1'st 2'nd
export function formatDateWithOrdinalAndShortMonth(isoString) {
  const dateObj = new Date(isoString);
  const day = dateObj.getDate();
  let ordinal = 'th';
  if (day % 10 === 1 && day !== 11) {
    ordinal = 'st';
  } else if (day % 10 === 2 && day !== 12) {
    ordinal = 'nd';
  } else if (day % 10 === 3 && day !== 13) {
    ordinal = 'rd';
  }

  const month = dateObj.toLocaleString('default', { month: 'short' });
  const year = dateObj.getFullYear();
  const time = dateObj.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' });

  return `${day}${ordinal} ${month}, ${year}, ${time}`;
}
// To remove Special character from string
function removeSpecialCharacters(str) {
  return str.replace(/[^a-zA-Z0-9\s]/g, '');
}

export function getShorthandName(fullName) {
  fullName = removeSpecialCharacters(fullName);

  const names = fullName.trim().split(" ");

  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  }

  let shorthand = "";

  shorthand += names[0].charAt(0).toUpperCase();

  for (let i = 1; i < names.length && shorthand.length < 4; i++) {
    shorthand += names[i].charAt(0).toUpperCase();
  }

  return shorthand;
}

export const handleKeyPress = (event) => {
  const key = event.key;
  if (event.keyCode !== 8 && (key < "0" || key > "9")) {
    event.preventDefault();
  }
};

// Create single Address line which is used in various cards
export const createAddress = (addressObj) => {
  const {
    address_line_1,
    address_line_2,
    postal_code,
    area,
    city,
    state,
    country,
  } = addressObj;

  const addressArray = [
    address_line_1,
    address_line_2,
    postal_code,
    area,
    city,
    state,
    country,
  ];

  const address = addressArray.filter((line) => line).join(", ");
  return address;
};
// Get the state Details using StateGeoID
export const fetchStates = async (stateGeoId) => {
  try {
    const response = await fetch(
      `https://secure.geonames.org/childrenJSON?geonameId=${stateGeoId}&username=teamprp`
    );
    const data = await response.json();

    return data.geonames;
  } catch (error) {
  }
};
// Fetch city details using CityGeoID
export const fetchCities = async (cityGeoId) => {
  try {
    const response = await fetch(
      `https://secure.geonames.org/childrenJSON?geonameId=${cityGeoId}&username=teamprp`
    );
    const data = await response.json();
    return data.geonames;
  } catch (error) {
  }
};

// It will add "..." after 50 words, help us to shorthand the word.
export const addEllipsisAfter50Words = (text, wordCount = 50) => {
  const words = text.split(" ");
  if (words.length <= wordCount) {
    return text;
  } else {
    const truncatedText = words.slice(0, wordCount).join(" ");
    return truncatedText + "...";
  }
};
// Get Country details By Country code
export function getCountryByCode(code) {
  let country =
    geoNames.find((country) => country.countryCode === code) || null;
  return country?.countryName || null;
}

// Check whether object is empty or not
export function isVerified(obj) {
  if (obj === null || obj === undefined) {
    return false;
  }
  return typeof obj === 'object' && Object.keys(obj).length !== 0;
}
// Common function to change the letter casing
export function convertLetterCase(inputString, caseType) {
  if (inputString) {
    switch (caseType.toLowerCase()) {
      case 'uppercase':
        return inputString.toUpperCase();
      case 'lowercase':
        return inputString.toLowerCase();
      case 'titlecase':
        return inputString.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
      default:
        return inputString; // If caseType is not recognized, return the input string as is
    }
  } else {
    return ""
  }
}
// Check logged in user is admin or not.
export const AdminChecker = () => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const currentURL = window.location.href;
  const parts = currentURL.split("/");
  const specificPart = parts[parts.length - 1];
  const checkAdmin = specificPart === currentEditingCompany.companyID
  return checkAdmin;
};
// Date formatting to keep dat standard all over the application
export const formatDateToISO = (date) => {
  return date.toISOString().slice(0, 10);
};

// Function to check if a date is greater than or equal to provided years ago
export const isDateGreaterThanProvidedYearsAgo = (inputDate, providedYear) => {
  // Create a new Date object for the current date
  const currentDate = new Date();

  // Calculate the date three years ago
  const threeYearsAgo = new Date(currentDate);
  threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - providedYear);

  // Convert the input date to a Date object if it's not already
  const inputDateObject = new Date(inputDate);

  // Compare the input date with three years ago
  return inputDateObject <= threeYearsAgo;
}
// To get current year
export function getCurrentYear() {
  return new Date().getFullYear();
}
// Formatting date to human readable like 21st of July
export const formatDateToHumanReadable = (dateString, dateFormat) => {
  const date = new Date(dateString);
  return format(date, dateFormat);
};
// Debounce function for search when difference between two key stroke is 500ms then only make search query.
export const debounce = (func) => {
  let timer;
  return function (...args) {
    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      func.apply(context, args);
    }, 500);
  };
};
// Email Validation
export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};
// To use react-select library we have added this formatting.
export const handleFormatOptions = (options) => {
  return options?.map(value => ({
    value: value,
    label: value
  }))
}
// Copy change show claim word to Access in code.
export function replaceClaimWithAccess(message) {
  return message.replace(/Claim/gi, 'Access');
}
// Html syntax handler when from backend it will pass string with HTML tags
export function HTMLTagsUsingDOMParser(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
}

export const handlePromptStripePayment = (link) => {
  const { REACT_APP_GPT_PROMPT_PAYMENT_LINK } = process.env;
  const routeLink = link ?? REACT_APP_GPT_PROMPT_PAYMENT_LINK;
  window.open(routeLink, "_blank", "noopener,noreferrer");
}
export const stripHtmlTags = (str) => {
  return str.replace(/<\/?[^>]+(>|$)/g, "");
};
export const handleCopy = (textToCopy, setCopied) => {
  navigator.clipboard.writeText(stripHtmlTags(textToCopy)).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  });
};
export function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // Smooth scroll animation
  });
}

export function cleanURL(url) {
  return url.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
}

export function isValidUrl(string) {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
}


export const customReactSelectStyles = {
  placeholder: (provided) => ({
    ...provided,
    textAlign: 'left',       // Ensure left alignment
    marginLeft: 2,           // Optional: adjust spacing
  }),
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderColor: state.isFocused ? '#9FA4A4' : '#9FA4A4',
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    textAlign: 'left',
    paddingLeft: '10px',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '40px',
    padding: '0 6px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px',
  }),
  indicatorSeparator: (state) => ({
    display: 'none',
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    height: '40px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
  }),
};

export const processIndustryData = (subIndustries) => {
  return subIndustries ? subIndustries?.map((industry) => {
    return { label: industry.sub_industry, value: industry.id, sequence: industry.sequence };
  }) : [];
}

export const buildVisibleQuestions = (questions_set, relatedMap, answers) => {
  const result = [];

  questions_set
    .sort((a, b) => a.question_order - b.question_order)
    .forEach(q => {
      result.push(q);

      const selected = answers[q.question_uid];
      const relatedIds = q.related_questions?.[selected] || [];

      relatedIds.forEach(id => {
        if (relatedMap[id]) {
          result.push(relatedMap[id]);
        }
      });
    });

  return result;
};

export const StatusBadge = ({ status }) => {
  const statusLower = status ? status.toLowerCase() : "";
  if (statusLower === "pending") {
    return (
      <span className="px-[6px] py-1 rounded-full text-[12px] bg-[#FFEDDD] text-[#F07910]">
        Pending
      </span>
    );
  }

  if (statusLower === "completed") {
    return (
      <span className="px-[6px] py-1 rounded-full text-[12px] bg-[#E8FFE6] text-[#2BB81F]">
        Completed
      </span>
    );
  }

  return (
    <span className="px-[6px] py-1 rounded-full text-[12px] bg-[#FFE8E8] text-[#F43737]">
      Expired
    </span>
  );
};

export const ActionButton = ({ status, fullWidth, onClick }) => {
  const statusLower = status ? status.toLowerCase() : "";
  return (
    <button
      onClick={onClick}
      className={`
        px-[14px] py-2 border border-[#D5D7DA] rounded-[8px] text-[14px] font-semibold text-[#414651]
        hover:bg-gray-50 transition
        ${fullWidth ? "w-full" : ""}
      `}
    >
      {statusLower === "completed"
        ? "View Reference"
        : "Give Reference"}
    </button>
  );
};

export function formatDateToReadable(isoString) {
  const date = new Date(isoString);

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric"
  });
}

export const EXCLUDED_KEYS = [
  "name",
  "designation",
  "employer_company_name"
];

// Capitalize first letter only
export const capitalizeFirstLetter = (text) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export function convertText(input) {
  return input.trim().toLowerCase().replace(/[\s.()]+/g, '_').replace(/[^a-z0-9_]/g, '').replace(/^_+|_+$/g, '');
}

const sanitizeFilename = (name) => {
  return name
    ?.replace(/[^a-zA-Z0-9._-]/g, '_')  // whitelist
    ?.replace(/_+/g, '_')
    ?.substring(0, 100)
    || 'file';
};

const getSafeExtension = (mimeType) => {
  const allowed = {
    'application/pdf': 'pdf',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'text/plain': 'txt',
  };

  return allowed[mimeType] || 'bin'; // fallback
};

export const onDownloadDocument = async (
  downloadLink,
  primaryID,
  secondaryID,
  fileName
) => {
  try {
    const response = await fetch(downloadLink);

    if (!response.ok) {
      throw new Error('Download failed');
    }

    const blob = await response.blob();

    // ✅ Safe extension (no blind trust on MIME split)
    const extension = getSafeExtension(blob.type);

    // ✅ Sanitize all inputs
    const safeFileName = sanitizeFilename(fileName);
    const safePrimaryID = sanitizeFilename(primaryID);
    const safeSecondaryID = sanitizeFilename(secondaryID);

    // ✅ Build filename safely
    let finalName = 'file';

    if (fileName) {
      finalName = safeFileName;
    } else if (primaryID && secondaryID) {
      finalName = `${safePrimaryID}_${safeSecondaryID}`;
    }

    const filename = `${finalName}.${extension}`;

    // ✅ Create object URL safely
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;

    // ✅ Use property instead of setAttribute
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();

    // ✅ Cleanup
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

export function convertFileSize(size) {
  if (size < 1024) {
    return size + " B";
  } else if (size < 1024 * 1024) {
    return (size / 1024).toFixed(2) + " KB";
  } else {
    return (size / (1024 * 1024)).toFixed(2) + " MB";
  }
}

export const parseWorkingString = (workingString) => {
  if (!workingString) return null;

  // Regular expression to match the pattern: "Monday to Friday 9AM to 6PM"
  // or variations like "Monday to Friday 09:00 AM to 06:00 PM"
  const regex = /^([A-Za-z]+)\s+to\s+([A-Za-z]+)\s+(\d{1,2}(?::\d{2})?\s*(?:AM|PM)?)\s+to\s+(\d{1,2}(?::\d{2})?\s*(?:AM|PM)?)$/i;

  const match = workingString.match(regex);

  if (!match) return null;

  const [, fromDayFull, toDayFull, openTimeRaw, closeTimeRaw] = match;

  // Map full day names to abbreviations
  const dayToAbbreviation = {
    "Monday": "Mon",
    "Tuesday": "Tue",
    "Wednesday": "Wed",
    "Thursday": "Thu",
    "Friday": "Fri",
    "Saturday": "Sat",
    "Sunday": "Sun"
  };

  // Get abbreviation (case insensitive)
  const fromDayAbbr = dayToAbbreviation[capitalizeFirstLetter(fromDayFull)];
  const toDayAbbr = dayToAbbreviation[capitalizeFirstLetter(toDayFull)];

  if (!fromDayAbbr || !toDayAbbr) return null;

  // Format time to "hh:mm AM/PM"
  const formatTimeString = (timeStr) => {
    if (!timeStr) return null;

    // Extract time and period
    let timePart = timeStr.trim();
    let period = "";

    // Check if period is already included
    if (/AM|PM/i.test(timePart)) {
      const matchPeriod = timePart.match(/(AM|PM)/i);
      period = matchPeriod[0].toUpperCase();
      timePart = timePart.replace(/AM|PM/i, "").trim();
    } else {
      // Default to AM if no period specified (assuming morning)
      period = "AM";
    }

    // Parse time
    let hour, minute = "00";

    if (timePart.includes(":")) {
      [hour, minute] = timePart.split(":");
    } else {
      hour = timePart;
    }

    hour = parseInt(hour, 10);
    minute = parseInt(minute, 10);

    // Validate hour range
    if (hour < 1 || hour > 12) {
      // Convert 24-hour format if needed
      if (hour > 12) {
        hour = hour - 12;
        period = "PM";
      } else if (hour === 0) {
        hour = 12;
        period = "AM";
      }
    }

    // Format with leading zeros
    const formattedHour = hour.toString().padStart(2, "0");
    const formattedMinute = minute.toString().padStart(2, "0");

    return `${formattedHour}:${formattedMinute} ${period}`;
  };

  const openTime = formatTimeString(openTimeRaw);
  const closeTime = formatTimeString(closeTimeRaw);

  if (!openTime || !closeTime) return null;

  return {
    fromDay: fromDayAbbr,
    toDay: toDayAbbr,
    openTime: openTime,
    closeTime: closeTime
  };
};